import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "sonner";

import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { dirKey, palletPlanKey } from "./ozonQueries";

/** Предупреждение у кнопки перезаписи — видно ДО нажатия и повторяется в подтверждении
 *  (решение владельца §7.9, 27.10.2026). */
export const OVERWRITE_WARNING =
  "До нажатия обязательно согласуйте со складом: перезапись заменит раскладку, которую склад " +
  "сделал в «Раскладке» портала, нашим планом паллет — собранное по ней может разойтись с планом.";

/** Кто держит живой план — словами, а не служебным `source` портала. */
const SOURCE: Record<string, string> = {
  seller_helper: "seller-helper",
  layout: "«Раскладка» склада",
  import_1c: "импорт из 1С",
  bot: "бот WB",
};

/** Записанный на поставку отказ «план держит склад» — крупно, красной плашкой над вкладками
 *  поставки (`d.onec.pallet_plan_conflict`, без похода в портал — виден и когда портал лежит):
 *  каскад правки на нём закончился, а план паллет в портале склада остался прежним. */
export function PalletPlanConflict({
  conflict,
}: {
  conflict: ozon.OzonPalletPlanConflict;
}): React.ReactElement {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
    >
      <div className="font-semibold">
        План паллет в портал склада НЕ записан — склад держит свой
        {conflict.live_revision !== null && (
          <>
            {" "}
            (живая ревизия {conflict.live_revision}
            {conflict.live_source &&
              `, ${SOURCE[conflict.live_source] ?? conflict.live_source}`}
            {conflict.our_revision !== null &&
              `; наша — ${conflict.our_revision}`}
            )
          </>
        )}
      </div>
      <div>{conflict.message}</div>
    </div>
  );
}

/** Перезапись плана склада нашим: красная кнопка с предупреждением, видным до нажатия, и шаг
 *  подтверждения (не `window.confirm`), повторяющий предупреждение. Только при `overridable`. */
function OverwritePlan({
  shipmentId,
}: {
  shipmentId: number;
}): React.ReactElement {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  // ⚠️ и при успехе, и при отказе перечитываем план и поставку: успех снимает записанный конфликт
  // (красная плашка над вкладками), отказ портала записывает новый
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: palletPlanKey(shipmentId) });
    void qc.invalidateQueries({ queryKey: dirKey(shipmentId) });
  };
  const overwrite = useMutation({
    mutationFn: () => ozon.overwriteOzonPalletPlan(shipmentId),
    onSuccess: (plan) => {
      setConfirming(false);
      refresh();
      toast.success(
        `Раскладка склада перезаписана нашим планом: ревизия ${plan.revision}`,
      );
    },
    onError: () => {
      setConfirming(false);
      refresh();
    },
  });
  return (
    <div className="space-y-1 rounded border border-destructive px-2 py-1">
      <div className="font-semibold text-destructive">{OVERWRITE_WARNING}</div>
      {confirming ? (
        <div
          role="alertdialog"
          aria-label="Подтверждение перезаписи раскладки склада"
          className="space-y-1"
        >
          <div>
            Точно перезаписать? Подтверждайте, только если склад согласен.
          </div>
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="destructive"
              disabled={overwrite.isPending}
              onClick={() => overwrite.mutate()}
            >
              Да, со складом согласовано — перезаписать
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={overwrite.isPending}
              onClick={() => setConfirming(false)}
            >
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => {
            overwrite.reset();
            setConfirming(true);
          }}
        >
          Перезаписать раскладку склада
        </Button>
      )}
      {overwrite.isError && (
        <div className="text-destructive">
          Раскладка склада не перезаписана:{" "}
          {extractErrorMessage(overwrite.error)}
        </div>
      )}
    </div>
  );
}

/** План паллет поставки в портале склада (`PALLETS_TARGET=portal`) — вместо номеров паллет 1С.
 *
 *  ⚠️ Портал не ответил — показываем ОШИБКУ, а не «плана нет»: пустой блок читался бы как
 *  «паллет у поставки нет». Конфликт (склад переложил план «Раскладкой») — красным, с текстом
 *  бэкенда: сам seller-helper поверх склада не пишет; перезаписать может человек красной кнопкой
 *  (`overridable`), согласовав со складом. */
export function PalletPlanBanner({
  shipmentId,
}: {
  shipmentId: number;
}): React.ReactElement {
  const q = useQuery({
    queryKey: palletPlanKey(shipmentId),
    queryFn: () => ozon.getOzonPalletPlan(shipmentId),
  });
  if (q.isPending) {
    return <div className="text-muted-foreground">План паллет: загрузка…</div>;
  }
  if (q.isError) {
    return (
      <div className="text-destructive">
        План паллет в портале склада не прочитан: {extractErrorMessage(q.error)}
      </div>
    );
  }
  const plan = q.data;
  return (
    <div className="space-y-1 rounded border px-2 py-1">
      <div>
        Паллеты — в портале склада
        {plan.revision !== null ? (
          <>
            {" "}
            · наша ревизия <b>{plan.revision}</b>
          </>
        ) : (
          " · план ещё не записан"
        )}
        {plan.live && plan.live.revision !== plan.revision && (
          <>
            {" "}
            · живая <b>{plan.live.revision}</b> (
            {SOURCE[plan.live.source ?? ""] ?? plan.live.source})
          </>
        )}
        {plan.pending && " · последняя запись без ответа — дошлём повтором"}
      </div>
      {plan.conflict && <div className="text-destructive">{plan.conflict}</div>}
      {plan.overridable && <OverwritePlan shipmentId={shipmentId} />}
      {plan.live && plan.live.pallets.length > 0 && (
        <ul className="space-y-0.5">
          {plan.live.pallets.map((p) => (
            <li key={`${p.position}-${p.sscc}`}>
              {p.place !== null ? `место №${p.place}` : "паллета склада"} · SSCC{" "}
              <b className="font-mono">{p.sscc ?? "—"}</b>
              {p.archive_number && <> · 1С {p.archive_number}</>}
              {p.boxes > 0 && (
                <>
                  {" "}
                  · коробов собрано {p.matched} из {p.boxes}
                </>
              )}
              {p.check !== null && <> · проверка №{p.check}</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
